/**
 * DashboardKpiStrip — the 4 main KPI tiles on Workspace Home (S08).
 *
 *   1. Cash position — sky stripe, with bank/cash/card breakdown footer
 *   2. Receivables — amber stripe, with overdue sub-line
 *   3. Sales · this month — emerald stripe (uses `paidThisMonthPaise`
 *      from briefings.today as the V1.0 proxy until the sales-revenue
 *      aggregator lands)
 *   4. GST due — rose stripe, links to /compliance/gst-returns
 *
 * Tile shape mirrors `features/master-list/StatTile` but adds the cash
 * breakdown footer (3 dot-coloured chips) the prototype shows beneath
 * the Cash KPI value.
 *
 * @see prototype/home/dashboard.html `.kpis` — V1.1 baseline
 */
import type { ReactNode } from "react";
import { Banknote, IndianRupee, TrendingUp, FileText } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { cn } from "@flint/ui";
import { paiseToInrShort } from "../../../lib/format.ts";

export interface DashboardKpiStripProps {
  cashPaise: string;
  /** Bank / petty / card breakdown for the Cash tile footer. */
  cashBreakdown: { bankPaise: string; cashPaise: string; cardPaise: string };
  receivablesTotalPaise: string;
  overduePaise: string;
  overdueInvoiceCount: number;
  /**
   * Sales for this calendar month. Today this reuses
   * `briefings.today.cash.balancePaise` (the V1.0 proxy — paid receipts
   * this month) until a real revenue aggregator lands.
   */
  salesThisMonthPaise: string;
  /** Compliance deadline label + days until — drives the GST tile. */
  complianceDeadlineLabel: string;
  complianceDaysUntil: number;
}

export function DashboardKpiStrip(props: DashboardKpiStripProps) {
  const overdueRatioPct = computeOverduePct(props.receivablesTotalPaise, props.overduePaise);

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3">
      <Tile
        tone="sky"
        icon={Banknote}
        label="Cash position"
        value={paiseToInrShort(props.cashPaise)}
        meta="across all accounts"
        footer={<CashBreakdownFooter {...props.cashBreakdown} />}
      />
      <Tile
        tone="warn"
        icon={IndianRupee}
        label="Receivables"
        value={paiseToInrShort(props.receivablesTotalPaise)}
        meta={
          props.overdueInvoiceCount === 0
            ? "No overdue invoices"
            : `${paiseToInrShort(props.overduePaise)} overdue${
                overdueRatioPct !== null ? ` · ${overdueRatioPct}%` : ""
              }`
        }
      />
      <Tile
        tone="ok"
        icon={TrendingUp}
        label="Sales · this month"
        value={paiseToInrShort(props.salesThisMonthPaise)}
        meta="received this calendar month"
      />
      <Tile
        tone="bad"
        icon={FileText}
        label={props.complianceDeadlineLabel}
        value={
          props.complianceDaysUntil >= 0
            ? `${props.complianceDaysUntil}d`
            : `${Math.abs(props.complianceDaysUntil)}d ago`
        }
        meta={props.complianceDaysUntil >= 0 ? "until next filing" : "past statutory due date"}
      />
    </div>
  );
}

// ── Tile + breakdown footer ────────────────────────────────────────

type Tone = "sky" | "warn" | "ok" | "bad";

const TONE: Record<Tone, { stripe: string; iconBg: string; iconFg: string }> = {
  sky: { stripe: "bg-sky-600", iconBg: "bg-sky-50", iconFg: "text-sky-600" },
  warn: { stripe: "bg-warn", iconBg: "bg-warn-50", iconFg: "text-warn" },
  ok: { stripe: "bg-ok", iconBg: "bg-ok-50", iconFg: "text-ok" },
  bad: { stripe: "bg-bad", iconBg: "bg-bad-50", iconFg: "text-bad" },
};

interface TileProps {
  tone: Tone;
  icon: LucideIcon;
  label: string;
  value: string;
  meta: string;
  footer?: ReactNode;
}

function Tile({ tone, icon: Icon, label, value, meta, footer }: TileProps) {
  const styles = TONE[tone];
  return (
    <div className="relative min-w-0 overflow-hidden bg-paper border border-line rounded-md shadow-sm transition-all hover:-translate-y-px hover:border-line-2 hover:shadow-md">
      <span
        aria-hidden
        className={cn("absolute inset-y-0 left-0 w-[3px] rounded-l-md", styles.stripe)}
      />
      <div className="px-4 py-3.5 pl-5">
        <div className="flex items-center justify-between gap-2 mb-1.5">
          <p className="text-xs text-muted min-w-0 truncate">{label}</p>
          <span
            aria-hidden
            className={cn(
              "inline-flex h-[26px] w-[26px] items-center justify-center rounded-md shrink-0",
              styles.iconBg,
              styles.iconFg,
            )}
          >
            <Icon size={14} strokeWidth={2} />
          </span>
        </div>
        <p className="text-2xl font-bold leading-tight num tracking-tight text-ink truncate">
          {value}
        </p>
        <p className="text-xs text-muted mt-1 truncate" title={meta}>
          {meta}
        </p>
        {footer}
      </div>
    </div>
  );
}

function CashBreakdownFooter({
  bankPaise,
  cashPaise,
  cardPaise,
}: {
  bankPaise: string;
  cashPaise: string;
  cardPaise: string;
}) {
  // Show items only when non-zero — keeps the footer honest when an account
  // type isn't set up yet.
  const hasBank = BigInt(bankPaise || "0") !== 0n;
  const hasCash = BigInt(cashPaise || "0") !== 0n;
  const hasCard = BigInt(cardPaise || "0") !== 0n;
  if (!hasBank && !hasCash && !hasCard) return null;
  return (
    <div className="mt-2 pt-2 border-t border-dashed border-line flex flex-wrap gap-x-2.5 gap-y-1 text-xs text-muted">
      {hasBank ? (
        <BreakdownChip dotClass="bg-sky-600" label="Banks" value={paiseToInrShort(bankPaise)} />
      ) : null}
      {hasCash ? (
        <BreakdownChip dotClass="bg-ok" label="Petty" value={paiseToInrShort(cashPaise)} />
      ) : null}
      {hasCard ? (
        <BreakdownChip dotClass="bg-bad" label="Card" value={paiseToInrShort(cardPaise)} />
      ) : null}
    </div>
  );
}

function BreakdownChip({
  dotClass,
  label,
  value,
}: {
  dotClass: string;
  label: string;
  value: string;
}) {
  return (
    <span className="inline-flex items-center gap-1.5 whitespace-nowrap">
      <span aria-hidden className={cn("w-1.5 h-1.5 rounded-full shrink-0", dotClass)} />
      {label} <span className="num text-ink-2 font-semibold text-xs">{value}</span>
    </span>
  );
}

/**
 * Overdue as a percentage of receivables — only computed when both sides
 * are non-zero. Returned as a whole-number percent for the meta line.
 */
function computeOverduePct(totalStr: string, overdueStr: string): number | null {
  const total = BigInt(totalStr || "0");
  const overdue = BigInt(overdueStr || "0");
  if (total === 0n || overdue === 0n) return null;
  // Integer math in basis points then divide — avoids float drift.
  return Number((overdue * 100n) / total);
}
